import { ThunderDispatcherV3 } from '@nu-art/thunderstorm-frontend/core/db-api-gen/types';
import { DBDef_RuntimeProtocol, generalId, PathwayStatus, RuntimeOrderStatus_Enum } from '@app/advisor-shared';
import { _keys, _values, flatArray, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import { ModuleFE_Advisor_Pathway, ModuleFE_Advisor_PathwayState, ModuleFE_Advisor_PathwayTransition } from '../../../modules/static-modules/pathway.js';
import { ModuleFE_Advisor_DiseaseProfile } from '../../../modules/static-modules/disease-profile.js';
import { sendValidateCacheMessage } from '../../../index.js';
import { ModuleFE_BaseAdvisorRuntime } from '../../../modules/ModuleFE_BaseAdvisorRuntime.js';
import { ModuleFE_AdvisorSession } from '../../advisor-session/index.js';
import { ModuleFE_RuntimeActiveNavPathway, ModuleFE_RuntimeActiveNavState } from '../runtime-navigation-info/index.js';
import { ModuleFE_RuntimeOrder } from '../runtime-order/index.js';
import { pathwayType_SingleState } from '@app/pathway-shared';
export const dispatch_onRuntimeProtocolsUpdated = new ThunderDispatcherV3('__onRuntimeProtocolsUpdated');
export class ModuleFE_RuntimeProtocol_Class extends ModuleFE_BaseAdvisorRuntime {
    _v1;
    __validateCache() {
        const encounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        const invalidProtocols = this.cache.filter(protocol => protocol.encounterId !== encounterId);
        const moduleName = this.getName();
        if (!invalidProtocols.length)
            return this.logDebug(`${moduleName} cache is valid`);
        this.logErrorBold(`${moduleName} has invalid data in cache`, invalidProtocols);
        return sendValidateCacheMessage(invalidProtocols, moduleName);
    }
    constructor() {
        super(DBDef_RuntimeProtocol, dispatch_onRuntimeProtocolsUpdated);
        this._v1 = {};
        this.registerVersionUpgradeProcessor('1.0.1', this.upgrade_101);
    }
    upgrade_101 = async (items) => {
        items.forEach(item => {
            if (!item.variableId)
                return;
            const protocol = item;
            protocol.variableIds = [item.variableId];
            protocol.careMapOutcomes = { [item.variableId]: item.careMapOutcome };
            // @ts-ignore
            delete item.variableId;
            delete item.careMapOutcome;
        });
    };
    init() {
        super.init();
        this.setCacheFilter(ModuleFE_AdvisorSession.encounterFilter);
    }
    getAllProtocols = () => {
        // filter general protocol out
        return this.cache.all().filter(protocol => protocol.variableIds[0] !== generalId);
    };
    getGeneralProtocol = () => {
        return this.cache.find(protocol => protocol.variableIds[0] === generalId);
    };
    getProtocolDBPathway = (protocolId) => {
        const protocol = this.cache.unique(protocolId);
        if (!protocol)
            throw new MUSTNeverHappenException(`cannot find runtime protocol with id ${protocolId}`);
        return ModuleFE_Advisor_Pathway.cache.unique(protocol.pathwayId);
    };
    getAllProtocolsForColorGroup = (colorGroupId) => {
        return sortArray(this.cache.allMutable().filter(rtProtocol => {
            const dps = ModuleFE_Advisor_DiseaseProfile.cache.filter(dp => rtProtocol.variableIds.includes(dp.variableId));
            return dps.some(dp => dp.colorGroup === colorGroupId);
        }), item => item.__created);
    };
    getProtocolActiveState = (rtProtocol) => {
        const protocolActivePathway = ModuleFE_RuntimeActiveNavPathway.cache.find(activePathway => activePathway.pathwayDbId === rtProtocol.pathwayId);
        if (!protocolActivePathway) {
            this.logError('cannot find active navigation information for pathway id', rtProtocol.pathwayId);
            return;
        }
        const activeState = ModuleFE_RuntimeActiveNavState.cache.unique(protocolActivePathway.activeState);
        if (!activeState) {
            this.logError('cannot find active state for state id', protocolActivePathway.activeState);
            return;
        }
        return activeState.stateDbId;
    };
    protocolIsDone = (protocol) => {
        if (protocol.variableIds[0] === generalId)
            return false;
        const pathway = ModuleFE_Advisor_Pathway.cache.unique(protocol.pathwayId);
        if (!pathway)
            throw new MUSTNeverHappenException(`Could not get pathway for id ${protocol.pathwayId} under runtime protocol with id ${protocol._id}`);
        const activePathway = ModuleFE_RuntimeActiveNavPathway.cache.find(ap => ap.pathwayDbId === pathway._id);
        const activeState = ModuleFE_RuntimeActiveNavState.cache.unique(activePathway?.activeState);
        const dbState = ModuleFE_Advisor_PathwayState.cache.unique(activeState?.stateDbId);
        switch (pathway.type) {
            //CC protocols are done when all of their connected rtOrders are at least ordered, i.e, no recommended orders are left
            case 'chief-complaint': {
                //Protocol is not at the end if not at an end state
                const outGoingTransitions = ModuleFE_Advisor_PathwayTransition.cache.filter(transition => transition.sourceId === dbState?._id);
                return !outGoingTransitions.length;
                // const rtOrders = ModuleFE_RuntimeOrder.cache.filter(rtOrder => _keys(rtOrder.protocolMapping ?? {}).includes(protocol.pathwayId));
                //If we didn't get orders for a CC protocol we are missing data for it
                // return !!rtOrders.length && rtOrders.every(rtOrder => rtOrder.orderStatus !== RuntimeOrderStatus_Enum.RECOMMENDED);
            }
            //Triage & DD protocols are "done" if they reached re-assessment
            case 'triage':
            case 'treatment':
            case 'differential-diagnosis': {
                return protocol.status === PathwayStatus.REACHED_REASSESSMENT;
            }
            //Single state protocols are not part of this game
            case pathwayType_SingleState: {
                const rtOrders = ModuleFE_RuntimeOrder.cache.filter(rtOrder => _keys(rtOrder.protocolMapping ?? {}).includes(protocol.pathwayId));
                return !!rtOrders.length && rtOrders.every(rtOrder => [RuntimeOrderStatus_Enum.RESULTED,
                    // @ts-ignore
                    RuntimeOrderStatus_Enum.COMPLETED].includes(rtOrder.orderStatus));
            }
            default:
                return false;
        }
    };
    /**
     * Sorts protocols by the lowest sorting index found in the care-map outcomes
     * @param protocols
     */
    sortProtocols = (protocols) => {
        return sortArray(protocols, protocol => {
            const cmos = _values(protocol.careMapOutcomes ?? {});
            return Math.min(...cmos.map(cmo => cmo.sortingIndex));
        });
    };
    getDPVsForProtocol = (protocol) => {
        const cmos = _values(protocol.careMapOutcomes ?? {});
        return flatArray(cmos.map(cmo => cmo.dpvsFindingsMap));
    };
}
export const ModuleFE_RuntimeProtocol = new ModuleFE_RuntimeProtocol_Class();
//# sourceMappingURL=ModuleFE_RuntimeProtocol.js.map